'use client';

import { useUser } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { GrowthBook } from '@growthbook/growthbook';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';
import {
  GrowthBookProvider,
  useGrowthBook,
} from '@growthbook/growthbook-react';
import { apiHost, clientKey, prefix } from '@web-app/constants/growthbook';
import { GrowthBookAttributes } from '@web-app/types/growthbook';
import { isProd } from '@web-app/utils/env.utils';
import { AppFeatures } from 'growthbook-types/app-features';
import { FC, ReactNode, useEffect, useRef } from 'react';

const pluginOptions = {};

/**
 * IdentifyGrowthBookUser Component
 * Identifies the logged-in user data to GrowthBook for user-specific feature flag targeting.
 * Sets clerkUserId and healthieUserId attributes when user is authenticated.
 * Note: This only sets initial user attributes. The organizationId is set later
 * by OrganizationProvider, which also manages the attributesSet state.
 */
export const IdentifyGrowthBookUser: FC = () => {
  const { user, isLoaded } = useUser();
  const growthbook = useGrowthBook();

  useEffect(() => {
    // Wait for both Clerk user data and GrowthBook to be ready
    if (!isLoaded || !growthbook) {
      return;
    }

    // Update the GrowthBookAttributes type manually when adding new attributes.
    const attributes: GrowthBookAttributes = {
      ...growthbook.getAttributes(),
      isProvider: Boolean(user?.publicMetadata?.healthie_user_id !== undefined),
      clerkUserId: user?.id ?? undefined,
      healthieUserId: user?.publicMetadata?.healthie_user_id as
        | string
        | undefined,
    };

    growthbook.setAttributes(attributes);
    datadogLogs.logger.info('GrowthBook user attributes set', {
      prefix,
      attributes,
    });
  }, [user, isLoaded, growthbook]);

  return null;
};

const GrowthBookHoc: FC<{ children: ReactNode }> = ({ children }) => {
  const gbRef = useRef(
    new GrowthBook<AppFeatures>({
      apiHost,
      clientKey,
      enableDevMode: !isProd,
      enabled: !!clientKey,
      plugins: [autoAttributesPlugin(pluginOptions)],
      onFeatureUsage: (featureKey, result) => {
        try {
          datadogRum.addFeatureFlagEvaluation(featureKey, result?.value);

          // TODO: pass this to rudderstack
          datadogLogs.logger.info('GrowthBook feature evaluated', {
            prefix,
            featureKey,
            value: result?.value,
          });
        } catch {}
      },
    }),
  );

  useEffect(() => {
    gbRef.current.init({ streaming: false }).catch((e) => {
      datadogLogs.logger.error('Error initializing GrowthBook', {
        error: e,
        prefix,
      });
    });
  }, []);

  return (
    <GrowthBookProvider growthbook={gbRef.current}>
      <IdentifyGrowthBookUser />
      {children}
    </GrowthBookProvider>
  );
};

export default GrowthBookHoc;
